import { $el } from '../elements';
import { onKey, UIKey } from '../key';
import { UIAutofill } from './input-base';

export interface UIAnyInputWrapper {
    element: HTMLElement;

    select(this: UIAnyInputWrapper, pos?: number | [number, number]): void;

    text: string;
    selectionStart: number;
    placeholder: string | undefined;
    required?: boolean;
    disabled?: boolean;
}

export interface UIInputWrapperOptions {
    focus_select?: boolean;
    blur_on_enter?: boolean;
    lines?: number;
    resizeable?: boolean;
    autofill?: UIAutofill | string;
    placeholder?: string;
    autoresize?: boolean;
    required?: boolean;
    disabled?: boolean;
    chars?: number;
    autocapitalize?: boolean;
    sensitive?: boolean;
}

const _autofillAutocomplete: AutoFill[] = ['name', 'given-name', 'additional-name', 'family-name', 'email', 'street-address', 'address-level2', 'postal-code', 'country', 'tel', 'username', 'current-password', 'current-password', 'new-password', 'off', 'one-time-code'];
const _autofillName = ['name', 'fname', 'mname', 'lname', 'email', 'address', 'city', 'zip', 'country', 'phone', 'username', 'password', 'old-password', 'password', 'confirm-password', 'one-time-code'];
const _autofillInputMode = ['text', 'text', 'text', 'text', 'email', 'text', 'text', 'text', 'text', 'tel', 'text', 'password', 'password', 'password', 'password', 'numeric'];

class UIEditableWrapper implements UIAnyInputWrapper {
    element: HTMLSpanElement;

    private _placeholder?: string;
    private _showing_placeholder?: boolean;
    private _set_listeners?: boolean;

    constructor(options: UIInputWrapperOptions) {
        const element = this.element = $el.SPAN();
        element.setAttribute('contenteditable', 'true');
        onKey(element, e => {
            if(e.key === UIKey.Enter) {
                if(options.blur_on_enter) {
                    element.blur();
                }
                return true;
            }
            return;
        });

        if(options.focus_select) {
            element.addEventListener('focus', () => {
                this.select();
            });
        }
    }

    select(pos?: number | [number, number]) {
        if(pos == undefined) {
            const range = document.createRange();
            range.selectNodeContents(this.element);
            const sel = window.getSelection()!;
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }

    get selectionStart() {
        return 0;
    }

    get text() {
        if(this._showing_placeholder)
            return '';
        return this.element.textContent || '';
    }

    set text(t: string) {
        const element = this.element;
        if(t && this._showing_placeholder) {
            this._showing_placeholder = false;
            element.classList.remove('f-placeholder');
        }
        element.textContent = t;
        this._updatePlaceholder();
    }

    get placeholder() {
        return this._placeholder;
    }

    set placeholder(p: string | undefined) {
        p = p || undefined;
        if(p === this._placeholder) return;

        this._placeholder = p;

        const element = this.element;

        if(p && !this._set_listeners) {
            element.addEventListener('blur', () => {
                this._updatePlaceholder();
            });

            element.addEventListener('focus', () => {
                if(this._showing_placeholder) {
                    element.textContent = '';
                    this._showing_placeholder = false;
                    element.classList.remove('f-placeholder');
                }
            });

            this._set_listeners = true;
        }

        this._updatePlaceholder();
    }

    private _updatePlaceholder() {
        const element = this.element;
        if(this._placeholder && !element.textContent) {
            element.textContent = this._placeholder;
            this._showing_placeholder = true;
            element.classList.add('f-placeholder');
        }
    }
}

class UIInputWrapper implements UIAnyInputWrapper {
    element: HTMLInputElement | HTMLTextAreaElement;

    constructor(options: UIInputWrapperOptions) {
        const element = this.element = (options.lines && options.lines > 1) || options.resizeable ? $el.TEXTAREA()
            : $el.INPUT(options.sensitive ? 'password' : 'text');

        if(options.autofill != undefined) {
            if(typeof options.autofill == 'number') {
                element.autocomplete = _autofillAutocomplete[options.autofill];
                element.name = _autofillName[options.autofill];
                element.inputMode = _autofillInputMode[options.autofill];
            } else {
                element.autocomplete = 'on';
                element.name = options.autofill;
            }
        } else {
            element.setAttribute('autocomplete', 'off');
        }

        if(options.chars != undefined) {
            element.style.width = 'calc(' + options.chars + 'ch + 16px)';
        }

        if(!options.autocapitalize)
            element.setAttribute('autocapitalize', 'none');

        if(options.focus_select) {
            element.addEventListener('focus', () => {
                this.select();
            });
        }

        if(options.blur_on_enter) {
            onKey(element, e => {
                if(e.key === UIKey.Enter) {
                    element.blur();
                    return true;
                }
                return;
            });
        }
    }

    select(pos?: number | [number, number]) {
        if(pos == undefined) {
            this.element.select();
        } else if(typeof pos == 'number') {
            this.element.setSelectionRange(pos, pos);
        } else {
            this.element.setSelectionRange(pos[0], pos[1]);
        }
    }

    get text() {
        return this.element.value || '';
    }

    set text(t: string) {
        this.element.value = t;
    }

    get placeholder() {
        return this.element.placeholder;
    }

    set placeholder(p: string) {
        this.element.placeholder = p;
    }

    set disabled(d: boolean) {
        if(d)
            this.element.setAttribute('disabled', '');
        else
            this.element.removeAttribute('disabled');
    }

    get selectionStart() {
        return this.element.selectionStart || 0;
    }
}

export function makeInputWrapper(options: UIInputWrapperOptions): UIAnyInputWrapper {
    const constructor = options.autoresize ? UIEditableWrapper : UIInputWrapper;
    const wrapper: UIAnyInputWrapper = new constructor(options);

    if(options.placeholder)
        wrapper.placeholder = options.placeholder;
    if(options.required)
        wrapper.required = true;
    if(options.disabled)
        wrapper.disabled = true;

    return wrapper;
}